import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import { ApiError } from '@/shared/lib/apiClient';
import type { CodeStatusResponseDto } from '@/shared/lib/apiTypes';

import { activateCode } from '../api/activateCode';

/**
 * 코드 활성화 mutation 훅의 입력 변수.
 *
 * @property userCode - 본인 유저 코드 (이미 발급된 상태여야 함)
 * @property code     - 사용자가 입력한 등록 대상 코드 (정규화된 4자리)
 */
export interface ActivateCodeVariables {
  userCode: string;
  code: string;
}

/**
 * 코드 등록(활성화) mutation 훅.
 *
 * 컴포넌트에선 `mutateAsync({ userCode, code })` 로 호출하고, 성공/실패 메시지는
 * 호출 측에서 처리한다(`onSuccess`/`onError` 옵션 주입). 응답으로 갱신된 누적
 * 카운트와 다운로드 URL 을 받아 redemption state 에 반영하는 책임은 호출 측에 있다.
 */
export function useActivateCode(options?: {
  onSuccess?: (data: CodeStatusResponseDto, variables: ActivateCodeVariables) => void;
  onError?: (error: ApiError, variables: ActivateCodeVariables) => void;
}): UseMutationResult<CodeStatusResponseDto, ApiError, ActivateCodeVariables> {
  return useMutation<CodeStatusResponseDto, ApiError, ActivateCodeVariables>({
    mutationFn: ({ userCode, code }) => activateCode(userCode, code),
    onSuccess: options?.onSuccess,
    onError: (error, variables) => {
      const normalized =
        error instanceof ApiError
          ? error
          : new ApiError('알 수 없는 오류가 발생했어요.', 0, undefined, error);
      options?.onError?.(normalized, variables);
    },
  });
}
